import { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined';
import { useAuth } from '../../context/authContext';
import { useBranchFilter } from '../../context/branchContext';
import { PageHeader } from '../../components/PageHeader';
import { FinanceFilters, FinancePaymentStatus, FinanceReport } from '../../types/finance';
import { ExportButtons } from './ExportButtons';
import { FinanceFilterBar } from './FinanceFilterBar';
import { PAYMENT_STATUS_LABEL } from './financeFormat';

const REPORTS: Array<{
  report: FinanceReport;
  title: string;
  description: string;
  dated: boolean;
}> = [
  {
    report: 'student-balances',
    title: 'Student balances',
    description:
      "Each student's Payment Checklist required, paid and outstanding amounts (base + SSCL) and payment status. Current balances — the date range doesn't apply.",
    dated: false,
  },
  {
    report: 'ledger',
    title: 'Payment ledger',
    description:
      'Every payment, discount, scholarship and refund in the date range, with its recorded SSCL, exchange rate and LKR amount.',
    dated: true,
  },
  {
    report: 'invoices',
    title: 'Invoices',
    description:
      'Invoices issued in the date range: type, amount, bank charges, exchange rate and LKR total.',
    dated: true,
  },
  {
    report: 'receipts',
    title: 'Receipts',
    description:
      'Receipts issued in the date range for confirmed payments, with amount, SSCL and LKR.',
    dated: true,
  },
];

/** Finance → Finance Reports (Phase 96): Excel/PDF exports of the finance
 * lists with shared filters. Branch scope is applied by the server. */
export const FinanceReportsPage = () => {
  const { hasPermission } = useAuth();
  const { selectedBranchId } = useBranchFilter();
  const [filters, setFilters] = useState<FinanceFilters>({});
  const [paymentStatus, setPaymentStatus] = useState<FinancePaymentStatus | ''>('');
  const base = { ...filters, branchId: selectedBranchId };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={AssessmentIcon}
        color="#AD1457"
        title="Finance Reports"
        subtitle="Download finance reports as Excel or PDF. Filters apply to every report below."
      />
      {!hasPermission('payments', 'export') && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your role can view finance data but not export it (payments:export).
        </Alert>
      )}
      <FinanceFilterBar value={filters} onChange={setFilters} />
      <Grid container spacing={2}>
        {REPORTS.map((r) => (
          <Grid item xs={12} md={6} key={r.report}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {r.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.description}
                  </Typography>
                  {r.report === 'student-balances' && (
                    <TextField
                      select
                      size="small"
                      label="Payment status"
                      sx={{ maxWidth: 220 }}
                      value={paymentStatus}
                      onChange={(e) =>
                        setPaymentStatus(e.target.value as FinancePaymentStatus | '')
                      }
                    >
                      <MenuItem value="">All</MenuItem>
                      {(Object.keys(PAYMENT_STATUS_LABEL) as FinancePaymentStatus[]).map((s) => (
                        <MenuItem key={s} value={s}>
                          {PAYMENT_STATUS_LABEL[s]}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <ExportButtons
                    params={
                      r.dated
                        ? { report: r.report, ...base }
                        : {
                            report: r.report,
                            ...base,
                            from: undefined,
                            to: undefined,
                            paymentStatus: paymentStatus || undefined,
                          }
                    }
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
